import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Send, MessageCircle, Mail, Info, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { SITE, CRM, waLink, mailLink } from "@/data/site";
import { SERVICES } from "@/data/services";
import { INDUSTRY_TYPES } from "@/data/industries";

const SERVICE_OPTS = [...SERVICES.map((s) => s.name), "Not sure yet — need advice"];
const BUSINESS_OPTS = [...new Set(INDUSTRY_TYPES.map((i) => i.name)), "Other"];
const BUDGETS = ["Under USD 1,000", "USD 1,000 – 3,000", "USD 3,000 – 7,500", "USD 7,500 – 15,000", "Above USD 15,000", "Not decided yet"];
const CONTACT_METHODS = ["WhatsApp", "Email", "Phone call"];

const phoneOk = (v) => /^[+\d\s()-]+$/.test(v) && v.replace(/\D/g, "").length >= 7 && v.replace(/\D/g, "").length <= 15;

// Name, email and phone are the only required fields. Everything else is optional.
const schema = z.object({
  fullName: z.string().trim().min(2, "Please enter your name").max(80, "Please keep your name under 80 characters"),
  email: z.string().trim().min(1, "Please enter your email").email("Please enter a valid email address"),
  phone: z.string().trim().min(1, "Please enter your phone number").refine(phoneOk, "Enter a valid phone number with 7 to 15 digits (you can use +, spaces and dashes)"),
  company: z.string().trim().max(100).optional(),
  country: z.string().trim().max(60).optional(),
  businessType: z.string().optional(),
  service: z.string().optional(),
  budget: z.string().optional(),
  details: z.string().trim().max(1500, "Please keep details under 1500 characters").optional(),
  contactMethod: z.string().nullish(),
});

/** Collects every named control of the form generically (no hard-coded field list). Empty fields are sent as "". */
const collectFields = (form) => {
  const fields = {};
  Array.from(form.elements).forEach((el) => {
    if (!el.name || el.disabled || el.type === "submit" || el.type === "button") return;
    if (el.type === "radio") {
      if (!(el.name in fields)) fields[el.name] = "";
      if (el.checked) fields[el.name] = el.value;
    } else if (el.type === "checkbox") {
      if (!(el.name in fields)) fields[el.name] = "";
      if (el.checked) fields[el.name] = fields[el.name] ? `${fields[el.name]}, ${el.value}` : el.value;
    } else {
      fields[el.name] = el.value;
    }
  });
  return fields;
};

const postLead = async (fields) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const res = await fetch(CRM.endpoint, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ key: CRM.key, fields, source_url: window.location.href, page_title: document.title }),
      signal: controller.signal,
    });
    const data = await res.json();
    return data && data.ok === true;
  } finally {
    clearTimeout(timer);
  }
};

const fieldCls = "mt-2 block w-full rounded-lg border border-ink/20 bg-white px-4 py-3 text-base text-ink placeholder:text-ink/45 transition-[border-color,box-shadow] focus:border-ink focus:outline-none focus:ring-2 focus:ring-lime";

const Field = ({ id, label, required, error, children }) => (
  <div>
    <label htmlFor={id} className="text-sm font-semibold text-ink">
      {label} {required ? <span className="text-coral" aria-hidden>*</span> : <span className="font-normal text-ink/55">(optional)</span>}
    </label>
    {children}
    {error && <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm font-medium text-[#B23A26]" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

export const InquiryForm = ({ defaultService = "" }) => {
  const formRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { service: defaultService },
  });

  const a11y = (name) => ({ id: `f-${name}`, "aria-invalid": errors[name] ? "true" : "false", "aria-describedby": errors[name] ? `f-${name}-error` : undefined, "data-testid": `input-${name}` });

  const onValid = async (_data, event) => {
    event?.preventDefault?.();
    const form = formRef.current || event?.target;
    setStatus("sending");
    try {
      const ok = await postLead(collectFields(form));
      if (ok) { setStatus("success"); reset({ service: defaultService }); } else setStatus("error");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div role="status" className="rounded-2xl border border-sage/40 bg-[#F1F4EE] p-8 md:p-12" data-testid="form-thank-you">
        <CheckCircle2 className="h-10 w-10 text-sageDeep" aria-hidden />
        <h3 className="mt-5 font-display text-2xl font-bold text-ink md:text-3xl">Thank you. We have received your inquiry.</h3>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-ink/75">Our team will review your details and get back to you shortly by phone, email or WhatsApp. If it is urgent, you can message us directly.</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-lime"><MessageCircle className="h-4 w-4" aria-hidden /> Chat on WhatsApp</a>
          <button type="button" onClick={() => setStatus("idle")} className="btn-ghost" data-testid="form-send-another">Send another inquiry</button>
        </div>
      </div>
    );
  }

  return (
    <form ref={formRef} noValidate onSubmit={handleSubmit(onValid)} className="rounded-2xl border border-ink/10 bg-white p-6 sm:p-8 md:p-10" data-testid="inquiry-form" aria-label="Project inquiry form">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field id="f-fullName" label="Full Name" required error={errors.fullName?.message}>
          <input type="text" autoComplete="name" className={fieldCls} {...register("fullName")} {...a11y("fullName")} />
        </Field>
        <Field id="f-email" label="Email" required error={errors.email?.message}>
          <input type="email" autoComplete="email" className={fieldCls} {...register("email")} {...a11y("email")} />
        </Field>
        <Field id="f-phone" label="Phone / WhatsApp" required error={errors.phone?.message}>
          <input type="tel" autoComplete="tel" placeholder="+91 70243 15567" className={fieldCls} {...register("phone")} {...a11y("phone")} />
        </Field>
        <Field id="f-company" label="Company Name" error={errors.company?.message}>
          <input type="text" autoComplete="organization" className={fieldCls} {...register("company")} {...a11y("company")} />
        </Field>
        <Field id="f-country" label="Country" error={errors.country?.message}>
          <input type="text" autoComplete="country-name" className={fieldCls} {...register("country")} {...a11y("country")} />
        </Field>
        <Field id="f-businessType" label="Business Type" error={errors.businessType?.message}>
          <select className={fieldCls} {...register("businessType")} {...a11y("businessType")}>
            <option value="">Select business type</option>
            {BUSINESS_OPTS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field id="f-service" label="Service Required" error={errors.service?.message}>
          <select className={fieldCls} {...register("service")} {...a11y("service")}>
            <option value="">Select a service</option>
            {SERVICE_OPTS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field id="f-budget" label="Budget Range" error={errors.budget?.message}>
          <select className={fieldCls} {...register("budget")} {...a11y("budget")}>
            <option value="">Select budget range</option>
            {BUDGETS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field id="f-details" label="Project Details" error={errors.details?.message}>
            <textarea rows={5} placeholder="Tell us about your business, goals and the features you have in mind. Please do not include health or medical information." className={fieldCls} {...register("details")} {...a11y("details")} />
          </Field>
        </div>
        <fieldset className="md:col-span-2">
          <legend className="text-sm font-semibold text-ink">Preferred Contact Method <span className="font-normal text-ink/55">(optional)</span></legend>
          <div className="mt-3 flex flex-wrap gap-3">
            {CONTACT_METHODS.map((m) => (
              <label key={m} className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-full border border-ink/20 px-5 text-sm font-semibold has-[:checked]:border-ink has-[:checked]:bg-ink has-[:checked]:text-white">
                <input type="radio" value={m} className="accent-lime" {...register("contactMethod")} data-testid={`contact-method-${m.toLowerCase().replace(/\s/g, "-")}`} /> {m}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button type="submit" disabled={status === "sending"} className="btn-lime disabled:cursor-not-allowed disabled:opacity-70" data-testid="form-submit">
          {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
          {status === "sending" ? "Sending…" : "Send Inquiry"}
        </button>
        <p className="text-sm text-ink/65"><span className="text-coral" aria-hidden>*</span> Name, email and phone are required.</p>
      </div>

      {status === "error" && (
        <div role="alert" className="mt-6 rounded-xl border border-[#E7B7AD] bg-[#FDF1EE] p-5 text-sm text-ink" data-testid="form-error">
          <p className="flex items-start gap-2 font-semibold"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#B23A26]" aria-hidden />Sorry, we could not send your inquiry. Please check your connection and try again.</p>
          <p className="mt-3 text-ink/75">You can also reach us directly:</p>
          <div className="mt-3 flex flex-wrap gap-3">
            <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2.5"><MessageCircle className="h-4 w-4" aria-hidden /> WhatsApp</a>
            <a href={mailLink("Athlevo IT Solution Project Inquiry")} className="btn-ghost !py-2.5"><Mail className="h-4 w-4" aria-hidden /> {SITE.email}</a>
          </div>
        </div>
      )}

      <p className="mt-6 flex gap-2 text-xs leading-relaxed text-ink/70" data-testid="form-privacy-notice">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>Your details are sent securely to our team so we can respond to your inquiry. We do not sell personal information. Please avoid sharing health or medical information. See our <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.</span>
      </p>
    </form>
  );
};
